import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, GraduationCap, Clock, Target, TrendingUp } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { srsManager } from '../utils/srsManager';

export function SentenceCategoriesGrid() {
  const { state, dispatch, actions } = useVocabulary();
  const { sentencesData } = state;
  const [stageStats, setStageStats] = useState({});

  const handleBackToExerciseTypes = () => {
    dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'exercise-type-selector' });
  };

  const handleStageSelect = (stage) => {
    dispatch({ type: actions.SET_SELECTED_STAGE, payload: stage });
    dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'sentence-subcategories' });
  };

  // Load SRS statistics for all sentence stages
  useEffect(() => {
    if (sentencesData && sentencesData.stage_categories && sentencesData.pools) {
      const stats = {};
      Object.entries(sentencesData.stage_categories).forEach(([stageKey, stageData]) => {
        // Aggregate stats for all pools in this stage
        let aggregatedStats = {
          total: 0,
          dueForReview: 0,
          tomorrow: 0,
          mastered: 0
        };
        
        if (stageData.pools) {
          stageData.pools.forEach(poolCode => {
            const poolInfo = sentencesData.pools[poolCode];
            const sentenceCount = poolInfo?.sentence_count || 0;
            const poolStat = srsManager.getPoolStats('sentences', poolCode, sentenceCount);
            aggregatedStats.total += poolStat.total;
            aggregatedStats.dueForReview += poolStat.dueForReview;
            aggregatedStats.tomorrow += poolStat.tomorrow;
            aggregatedStats.mastered += poolStat.mastered;
          });
        }
        
        stats[stageKey] = aggregatedStats;
      });
      setStageStats(stats);
    }
  }, [sentencesData]);

  const getStageIcon = (stage) => {
    const icons = {
      '01': BookOpen,
      '02': GraduationCap
    };
    return icons[stage] || BookOpen;
  };

  // Get stage categories from sentences data
  const getStageCategories = () => {
    if (!sentencesData || !sentencesData.stage_categories) {
      return [];
    }
    
    const stageCategories = sentencesData.stage_categories;
    
    return Object.entries(stageCategories).map(([stageKey, stageData]) => {
      return {
        key: stageKey,
        name: stageData.name,
        description: stageData.description,
        color: stageKey === '01' ? 'blue' : 'green',
        pools: stageData.pools || [],
        poolCount: stageData.pools?.length || 0
      };
    }).sort((a, b) => (stageCategories[a.key]?.order || 0) - (stageCategories[b.key]?.order || 0));
  };

  const stageCategories = getStageCategories();

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={handleBackToExerciseTypes}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          title="Powrót do wyboru typu ćwiczenia"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Wybierz etap nauki
          </h1>
          <p className="text-gray-600">
            Każdy etap zawiera 3 pule zdań o rosnącym poziomie trudności
          </p>
        </div>
      </div>

      {/* Stage Categories Grid */}
      <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6">
        {stageCategories.map((stage) => {
          const IconComponent = getStageIcon(stage.key);
          const colorClasses = {
            blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300',
            green: 'bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300'
          };
          const iconColorClasses = {
            blue: 'bg-blue-600',
            green: 'bg-green-600'
          };
          
          return (
            <button
              key={stage.key}
              onClick={() => handleStageSelect(stage.key)}
              className={`p-6 ${colorClasses[stage.color]} border-2 rounded-xl transition-all duration-200 text-left group`}
            >
              <div className="flex items-center mb-4">
                <div className={`p-3 ${iconColorClasses[stage.color]} text-white rounded-lg group-hover:scale-110 transition-transform`}>
                  <IconComponent size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 ml-4">
                  {stage.name}
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                {stage.description}
              </p>
              
              {/* Pool Count */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Dostępne pule:</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                    {stage.poolCount}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {stage.pools.map(poolCode => (
                    <span key={poolCode} className="text-xs bg-white text-gray-600 px-2 py-1 rounded border">
                      {poolCode}
                    </span>
                  ))}
                </div>
              </div>

              {/* SRS Stats */}
              {stageStats[stage.key] && (
                <div className="p-3 bg-white/50 rounded-lg border border-gray-200">
                  <div className="text-sm font-medium text-gray-700 mb-2">Postęp uczenia:</div>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-orange-600 mb-1">
                        <Clock size={14} />
                        <span className="font-semibold text-sm">{stageStats[stage.key].dueForReview}</span>
                      </div>
                      <div className="text-gray-600">Do powtórki</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                        <Target size={14} />
                        <span className="font-semibold text-sm">{stageStats[stage.key].tomorrow}</span>
                      </div>
                      <div className="text-gray-600">Na jutro</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                        <TrendingUp size={14} />
                        <span className="font-semibold text-sm">{stageStats[stage.key].mastered}</span>
                      </div>
                      <div className="text-gray-600">Opanowane</div>
                    </div>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading/Empty state */}
      {stageCategories.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500">
            <span className="text-4xl block mb-2">📚</span>
            Ładowanie etapów nauki...
          </div>
        </div>
      )}
    </div>
  );
}
